/* crm-analytics-p3.jsx — Enhanced analytics dashboards for Phase 3
   Provides: AnalyticsP3View
   Self-registers on window. */

const { useState, useEffect, useMemo, useRef, useCallback } = React;

const fmtMoney = (n) => '$' + (n >= 1_000_000 ? (n / 1_000_000).toFixed(1) + 'M' : n >= 1_000 ? (n / 1_000).toFixed(0) + 'K' : Math.round(n || 0));

/* ══════════════════════════════════════════════════════════════
   SALES FUNNEL SVG
   ══════════════════════════════════════════════════════════════ */
const SalesFunnelChart = ({ stages, deals }) => {
  const data = useMemo(() => {
    return stages
      .filter(s => !/\b(lost)\b/i.test(s.name))
      .map(s => ({
        name: s.name,
        count: deals.filter(d => d.stageId === s.id).length,
        value: deals.filter(d => d.stageId === s.id).reduce((sum, d) => sum + (d.value || 0), 0),
        isWon: /\bwon\b/i.test(s.name),
        color: s.color || s.tone || '#6366f1',
      }));
  }, [stages, deals]);

  const maxCount = Math.max(...data.map(d => d.count), 1);
  const W = 280, barH = 30, gap = 8;
  const totalH = data.length * (barH + gap);

  return (
    <svg width="100%" viewBox={`0 0 ${W + 120} ${totalH + 10}`} style={{ overflow: 'visible' }}>
      {data.map((d, i) => {
        const pct = d.count / maxCount;
        const barW = Math.max(8, Math.round(pct * W));
        const x = (W - barW) / 2;
        const y = i * (barH + gap);
        return (
          <g key={d.name}>
            <rect x={x} y={y} width={barW} height={barH} rx={6}
              fill={d.isWon ? 'var(--nx-success)' : d.color}
              opacity={0.85}
            />
            <text x={x - 6} y={y + barH / 2 + 4} textAnchor="end" fontSize={10} fill="var(--nx-text-muted)">{d.name}</text>
            <text x={x + barW + 6} y={y + barH / 2 + 4} textAnchor="start" fontSize={10} fill="var(--nx-text-muted)">{d.count} · {fmtMoney(d.value)}</text>
          </g>
        );
      })}
    </svg>
  );
};

/* ══════════════════════════════════════════════════════════════
   WIN RATE GAUGE (half-circle SVG)
   ══════════════════════════════════════════════════════════════ */
const WinRateGauge = ({ winRate }) => {
  const r = 60, cx = 80, cy = 80;
  const circumference = Math.PI * r;
  const offset = circumference - (winRate / 100) * circumference;
  const color = winRate >= 60 ? 'var(--nx-success)' : winRate >= 35 ? '#F59E0B' : 'var(--nx-danger)';
  return (
    <svg width={160} height={95} viewBox="0 0 160 95">
      <path d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`} fill="none" stroke="var(--nx-border)" strokeWidth={12} strokeLinecap="round" />
      <path d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
        fill="none" stroke={color} strokeWidth={12} strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        style={{ transition: 'stroke-dashoffset 0.5s ease' }}
      />
      <text x={cx} y={cy - 8} textAnchor="middle" fontSize={22} fontWeight={800} fill={color}>{winRate}%</text>
      <text x={cx} y={cy + 10} textAnchor="middle" fontSize={10} fill="var(--nx-text-muted)">Win Rate</text>
    </svg>
  );
};

/* ══════════════════════════════════════════════════════════════
   REVENUE BY OWNER (horizontal bars)
   ══════════════════════════════════════════════════════════════ */
const RevenueByOwnerChart = ({ ownerData }) => {
  const max = Math.max(...ownerData.map(o => o.value), 1);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {ownerData.slice(0, 8).map((o, i) => {
        const pct = Math.round((o.value / max) * 100);
        return (
          <div key={o.owner} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Avatar name={o.owner} size={22} tone="var(--color-primary-500)" style={{ flexShrink: 0 }} />
            <span style={{ fontSize: 11, minWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.owner}</span>
            <div style={{ flex: 1, height: 12, background: 'var(--nx-border)', borderRadius: 6, overflow: 'hidden' }}>
              <div style={{ width: pct + '%', height: '100%', background: `hsl(${220 + i * 25}, 70%, 55%)`, borderRadius: 6, transition: 'width 0.4s ease' }} />
            </div>
            <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--nx-accent)', minWidth: 50, textAlign: 'right' }}>{fmtMoney(o.value)}</span>
          </div>
        );
      })}
      {ownerData.length === 0 && <span style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>No data</span>}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════
   ACTIVITY HEATMAP (7 days × 4 weeks)
   ══════════════════════════════════════════════════════════════ */
const ActivityHeatmap = ({ activity }) => {
  const DAYS  = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const WEEKS = 4;
  const now   = new Date();

  const grid = useMemo(() => {
    const map = {};
    activity.forEach(a => {
      const d = new Date(a.createdAt || a.date || 0);
      const diffDays = Math.floor((now - d) / 86_400_000);
      if (diffDays < WEEKS * 7) {
        const key = diffDays;
        map[key] = (map[key] || 0) + 1;
      }
    });
    const cells = [];
    for (let w = WEEKS - 1; w >= 0; w--) {
      for (let day = 0; day < 7; day++) {
        const daysBack = w * 7 + (6 - now.getDay() + day + 1) % 7;
        cells.push({ w: WEEKS - 1 - w, day, count: map[daysBack] || 0 });
      }
    }
    return cells;
  }, [activity]);

  const maxCount = Math.max(...grid.map(c => c.count), 1);
  const cellSize = 18, gap = 3;

  return (
    <svg width={WEEKS * (cellSize + gap) + 30} height={7 * (cellSize + gap) + 20} style={{ overflow: 'visible' }}>
      {DAYS.map((d, i) => (
        <text key={d} x={0} y={i * (cellSize + gap) + cellSize / 2 + 5} fontSize={9} fill="var(--nx-text-muted)">{d}</text>
      ))}
      {grid.map((c, i) => {
        const x = c.w * (cellSize + gap) + 26;
        const y = c.day * (cellSize + gap);
        const intensity = c.count / maxCount;
        const opacity = c.count === 0 ? 0.1 : 0.2 + intensity * 0.8;
        return (
          <rect key={i} x={x} y={y} width={cellSize} height={cellSize} rx={3}
            fill="var(--nx-accent)" opacity={opacity}
          >
            <title>{c.count} activities</title>
          </rect>
        );
      })}
    </svg>
  );
};

/* ══════════════════════════════════════════════════════════════
   PIPELINE AGING HISTOGRAM
   ══════════════════════════════════════════════════════════════ */
const AgingHistogram = ({ deals }) => {
  const BUCKETS = [
    { label: '0–7d',   min: 0,  max: 7   },
    { label: '8–30d',  min: 7,  max: 30  },
    { label: '31–90d', min: 30, max: 90  },
    { label: '90+d',   min: 90, max: Infinity },
  ];
  const data = BUCKETS.map(b => ({
    label: b.label,
    count: deals.filter(d => (d.age || 0) > b.min && (d.age || 0) <= b.max).length,
    color: b.min === 0 ? '#10B981' : b.min === 7 ? '#6366f1' : b.min === 30 ? '#F59E0B' : '#EF4444',
  }));
  const maxCount = Math.max(...data.map(d => d.count), 1);
  const barW = 44, gap = 12, H = 80;

  return (
    <svg width={(barW + gap) * data.length + 20} height={H + 30} style={{ overflow: 'visible' }}>
      {data.map((d, i) => {
        const barH = Math.max(2, Math.round((d.count / maxCount) * H));
        const x = i * (barW + gap) + 10;
        const y = H - barH;
        return (
          <g key={d.label}>
            <rect x={x} y={y} width={barW} height={barH} rx={4} fill={d.color} opacity={0.85} />
            <text x={x + barW / 2} y={H + 14} textAnchor="middle" fontSize={10} fill="var(--nx-text-muted)">{d.label}</text>
            {d.count > 0 && <text x={x + barW / 2} y={y - 4} textAnchor="middle" fontSize={10} fill="var(--nx-text-muted)">{d.count}</text>}
          </g>
        );
      })}
    </svg>
  );
};

/* ══════════════════════════════════════════════════════════════
   ANALYTICS P3 VIEW
   ══════════════════════════════════════════════════════════════ */
const AnalyticsP3View = () => {
  const store    = useStore();
  const pipeline = store.pipeline  || [];
  const allDeals = store.deals     || [];
  const activity = store.activity  || [];

  const activeDeals = useMemo(() =>
    allDeals.filter(d => {
      if (d.archived) return false;
      const s = pipeline.find(x => x.id === d.stageId);
      if (!s) return false;
      return s.kind === 'OPEN' || (!s.kind && !/\b(won|lost)\b/i.test(s.name));
    }),
    [allDeals, pipeline]
  );

  /* Win rate */
  const { winRate, ownerData, teamRows } = useMemo(() => {
    const wonDeals  = allDeals.filter(d => { const s = pipeline.find(x => x.id === d.stageId); return s && /\bwon\b/i.test(s.name); });
    const lostDeals = allDeals.filter(d => { const s = pipeline.find(x => x.id === d.stageId); return s && /\blost\b/i.test(s.name); });
    const closed    = wonDeals.length + lostDeals.length;
    const wr        = closed ? Math.round((wonDeals.length / closed) * 100) : 0;

    /* By owner */
    const ownerMap = {};
    activeDeals.forEach(d => {
      const o = d.owner || 'Unassigned';
      if (!ownerMap[o]) ownerMap[o] = { owner: o, value: 0 };
      ownerMap[o].value += d.value || 0;
    });
    const od = Object.values(ownerMap).sort((a, b) => b.value - a.value);

    /* Team table */
    const teamMap = {};
    allDeals.forEach(d => {
      const o  = d.owner || 'Unassigned';
      const s  = pipeline.find(x => x.id === d.stageId);
      const isW = s && /\bwon\b/i.test(s.name);
      const isL = s && /\blost\b/i.test(s.name);
      if (!teamMap[o]) teamMap[o] = { owner: o, won: 0, lost: 0, pipeline: 0, avgSize: 0, actCount: 0, totalPipe: 0, totalWon: 0 };
      if (isW) { teamMap[o].won++; teamMap[o].totalWon += d.value || 0; }
      else if (isL) { teamMap[o].lost++; }
      else { teamMap[o].pipeline++; teamMap[o].totalPipe += d.value || 0; }
    });
    activity.forEach(a => {
      const o = a.owner || a.who || 'Unassigned';
      if (teamMap[o]) teamMap[o].actCount++;
    });
    const rows = Object.values(teamMap).map(r => ({
      ...r,
      winPct: (r.won + r.lost) > 0 ? Math.round((r.won / (r.won + r.lost)) * 100) : 0,
      avgSize: r.won > 0 ? Math.round(r.totalWon / r.won) : 0,
    })).sort((a, b) => b.won - a.won);

    return { winRate: wr, ownerData: od, teamRows: rows };
  }, [allDeals, activeDeals, pipeline, activity]);

  return (
    <div style={{ padding: 'var(--space-5)', maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ marginBottom: 'var(--space-5)' }}>
        <h1 style={{ fontSize: 20, fontWeight: 800, margin: 0 }}>Advanced Analytics</h1>
        <p style={{ fontSize: 12, color: 'var(--nx-text-muted)', margin: '4px 0 0' }}>Visual pipeline and team performance dashboards.</p>
      </div>

      {/* Row 1: Funnel + Win Rate + Activity Heatmap */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 20, marginBottom: 20 }}>
        <div style={{ background: 'var(--nx-surface-alt)', borderRadius: 12, padding: 16, border: '1px solid var(--nx-border)' }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12 }}>Sales Funnel</div>
          <SalesFunnelChart stages={pipeline} deals={allDeals} />
        </div>
        <div style={{ background: 'var(--nx-surface-alt)', borderRadius: 12, padding: 16, border: '1px solid var(--nx-border)', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12, alignSelf: 'flex-start' }}>Win Rate</div>
          <WinRateGauge winRate={winRate} />
          <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginTop: 4 }}>
            {winRate >= 60 ? 'Above target' : winRate >= 35 ? 'Near target' : 'Below target'}
          </div>
        </div>
        <div style={{ background: 'var(--nx-surface-alt)', borderRadius: 12, padding: 16, border: '1px solid var(--nx-border)' }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12 }}>Activity Heatmap</div>
          <div style={{ overflowX: 'auto' }}>
            <ActivityHeatmap activity={activity} />
          </div>
          <div style={{ fontSize: 10, color: 'var(--nx-text-muted)', marginTop: 6 }}>Last 4 weeks</div>
        </div>
      </div>

      {/* Row 2: Revenue by Owner + Pipeline Aging */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
        <div style={{ background: 'var(--nx-surface-alt)', borderRadius: 12, padding: 16, border: '1px solid var(--nx-border)' }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12 }}>Revenue by Owner</div>
          <RevenueByOwnerChart ownerData={ownerData} />
        </div>
        <div style={{ background: 'var(--nx-surface-alt)', borderRadius: 12, padding: 16, border: '1px solid var(--nx-border)' }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12 }}>Pipeline Aging</div>
          <div style={{ overflowX: 'auto' }}>
            <AgingHistogram deals={activeDeals} />
          </div>
        </div>
      </div>

      {/* Row 3: Team Performance Table */}
      <div style={{ background: 'var(--nx-surface-alt)', borderRadius: 12, padding: 16, border: '1px solid var(--nx-border)', overflowX: 'auto' }}>
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12 }}>Team Performance</div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr>
              {['Owner', 'Won', 'Lost', 'Win Rate', 'Avg Deal Size', 'Pipeline Value', 'Activities'].map(h => (
                <th key={h} style={{ textAlign: 'left', padding: '6px 10px', fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', background: 'var(--nx-surface-alt)' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {teamRows.map(r => (
              <tr key={r.owner} style={{ borderTop: '1px solid var(--nx-border)' }}>
                <td style={{ padding: '8px 10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Avatar name={r.owner} size={22} tone="var(--color-primary-500)" />
                    <span style={{ fontWeight: 500 }}>{r.owner}</span>
                  </div>
                </td>
                <td style={{ padding: '8px 10px', fontWeight: 600, color: 'var(--nx-success)' }}>{r.won}</td>
                <td style={{ padding: '8px 10px', color: 'var(--nx-danger)' }}>{r.lost}</td>
                <td style={{ padding: '8px 10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <div style={{ width: 36, height: 4, background: 'var(--nx-border)', borderRadius: 2 }}>
                      <div style={{ width: r.winPct + '%', height: '100%', background: r.winPct >= 50 ? 'var(--nx-success)' : r.winPct >= 30 ? '#F59E0B' : 'var(--nx-danger)', borderRadius: 2 }} />
                    </div>
                    <span>{r.winPct}%</span>
                  </div>
                </td>
                <td style={{ padding: '8px 10px' }}>{fmtMoney(r.avgSize)}</td>
                <td style={{ padding: '8px 10px', color: 'var(--nx-accent)' }}>{fmtMoney(r.totalPipe)}</td>
                <td style={{ padding: '8px 10px' }}>{r.actCount}</td>
              </tr>
            ))}
            {teamRows.length === 0 && (
              <tr><td colSpan={7} style={{ padding: 16, textAlign: 'center', color: 'var(--nx-text-muted)', fontSize: 12 }}>No team data available.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
window.AnalyticsP3View = AnalyticsP3View;
